import type {NewProjectOrigin} from "@buildwithflux/core";
import type {
    Enterprise,
    EnterpriseUid,
    DocumentUid,
    AccountUid,
    OrganizationUid,
    Organization,
    UserUid,
} from "@buildwithflux/models";

import {OnPlanChosenCallback} from "components/common/components/dialog/ChoosePlanDialogContent";

/**
 * Enum of all dialog types in the application
 */
export enum DialogType {
    IMPORT_PART = 1,
    PUBLISH_PART = 2,
    PART_UPDATES = 3,
    CREATE_ORGANIZATION = 4,
    ORGANIZATION_ADD_MEMBERS = 5,
    SHADOWBAN = 6,
    USER_PLANS_AND_PAYMENTS = 7,
    FOLLOWERS = 8,
    FOLLOWING = 9,
    IMPORT_DATASHEET_PART = 10,
    NEW_PROJECT = 11,
    CHOOSE_YOUR_PLAN = 12,
    RESTORE_PROJECT = 13,
    JEP30 = 14,
    API_KEYS = 15,
    EXPORT_MANUFACTURING_FILES_DIALOG = 17,
    FREE_PLAN_ANNOUNCEMENT = 18,
    USER_CANCEL_PLAN = 19,
    POLYGON_ANNOUNCEMENT = 20,
    CREATE_PART_WIZARD = 21,
}

/**
 * Interface for a dialog instance with its type and props
 */
export interface IDialog<T extends DialogType> {
    type: T;
    props: IDialogProps<T>;
}

// Any customized and needed props for dialogs can be put here.
export type IDialogProps<T extends DialogType> = T extends DialogType.IMPORT_PART
    ? {
          filesToImport: File[];
          organizationOwnerUid: OrganizationUid | undefined;
      }
    : T extends DialogType.IMPORT_DATASHEET_PART
    ? {
          organizationOwnerUid: OrganizationUid | undefined;
      }
    : T extends DialogType.ORGANIZATION_ADD_MEMBERS
    ? {
          organization: Organization;
          enterprise: Enterprise | undefined;
      }
    : T extends DialogType.CREATE_ORGANIZATION
    ? {
          /**
           * Callback when the dialog is closed without creating a new organization
           */
          onClose?: () => void;

          /**
           * Callback when the dialog is closed after creating a new organization
           */
          onCreate?: (organization: Organization) => void;
      }
    : T extends DialogType.FOLLOWERS
    ? {
          accountUid: AccountUid;
      }
    : T extends DialogType.FOLLOWING
    ? {
          accountUid: AccountUid;
      }
    : T extends DialogType.NEW_PROJECT
    ? {
          origin: NewProjectOrigin;
          organizationUid: OrganizationUid | undefined;
          enterpriseUid: EnterpriseUid | undefined;
      }
    : T extends DialogType.CHOOSE_YOUR_PLAN
    ? {onPlanChosen?: OnPlanChosenCallback}
    : T extends DialogType.RESTORE_PROJECT
    ? {documentUid?: DocumentUid}
    : T extends DialogType.SHADOWBAN
    ? {
          objectType: "user" | "part";
          uid: UserUid | string | undefined;
          name: string | undefined;
      }
    : T extends DialogType.FREE_PLAN_ANNOUNCEMENT
    ? {serveAs: "announcement" | "info"}
    : T extends DialogType.USER_CANCEL_PLAN
    ? {cancelPlanUrl: string}
    : T extends DialogType.CREATE_PART_WIZARD
    ? {
          organizationUid: OrganizationUid | undefined;
          enterpriseUid: EnterpriseUid | undefined;
      }
    : never;

/**
 * @deprecated Use DialogsStoreState from dialogs.ts instead
 */
export interface IDialogState {
    openedDialogs: IDialog<DialogType>[];
}
